/**
 * Adaptador puro: converte a `GradeAndFrequenceViewModel` (tag
 * `class-planning-grade-and-frequence-configuration`) no modelo de leitura da tela
 * (`ClassGroup`), preenchendo com defaults os campos **UI-only** que o backend ainda não expõe.
 *
 * O GET agora expõe `disciplineId` e os flags de controle
 * (`showOnStudentDocument`, `isIncludedInGradeCalculation`,
 * `isIncludedInAttendanceCalculation`) por disciplina — usados para `refId`
 * (persistência dos toggles via PATCH) e para o estado inicial dos botões.
 *
 * Campos ainda UI-only preenchidos com defaults: tipos de atividade além de
 * Disciplina, nota/frequência padrão e peso de módulo.
 */

import type {
  Activity,
  ClassGroup,
  Discipline,
  GradeAndFrequenceViewModel,
  Lesson,
  Module,
} from '../types/gradeConfig';
import { EActivityType } from '../types/gradeConfig';

/** Metadados da turma selecionada, vindos do dropdown de busca (`class-students`). */
export interface ClassGroupMeta {
  /** `classStudentId` selecionado (usado como `ClassGroup.id`). */
  readonly id: string;
  /** Rótulo exibido da turma (valor do `DropDownListViewModel`). */
  readonly name: string;
  /** Curso, quando disponível (o backend não o retorna isoladamente hoje). */
  readonly course?: string;
}

/**
 * Converte a view model remota em um `ClassGroup` pronto para os cards.
 *
 * @param vm - Configuração de nota/frequência retornada pela API.
 * @param meta - Identidade/rótulo da turma selecionada.
 * @returns Turma no formato de leitura consumido pela UI (com defaults UI-only).
 */
export function adaptGradeAndFrequence(
  vm: GradeAndFrequenceViewModel | undefined,
  meta: ClassGroupMeta,
): ClassGroup {
  const modules: Module[] = [];
  const disciplines: Discipline[] = [];
  const activities: Activity[] = [];

  const vmModules = vm?.modules ?? [];
  vmModules.forEach((mod, mi) => {
    const moduleId = String(mod.id ?? `m_${mi}`);
    // Módulos não têm peso (conceito removido da UI/estado).
    modules.push({ id: moduleId, name: mod.name ?? '' });

    (mod.disciplines ?? []).forEach((disc, di) => {
      // Id real da disciplina (o GET agora o expõe); fallback sintético apenas se
      // ausente, para manter uma chave local estável. `refId` real permite que os
      // toggles (documento/nota/frequência) persistam via PATCH.
      const discId = disc.disciplineId != null ? String(disc.disciplineId) : `disc_${mi}_${di}`;
      const lessons: Lesson[] = (disc.classes ?? []).map((cls, ci) => ({
        id: String(cls.id ?? `${discId}_c${ci}`),
        name: cls.name ?? '',
        date: cls.startDate ?? '',
        status: cls.status,
        showInDocument: true,
        countsInGrade: true,
        countsInAttendance: true,
        attendanceByView: true,
        exam: {
          id: String(cls.examInfo?.id ?? `${discId}_p${ci}`),
          name: cls.examInfo?.name ?? '',
          weight: cls.examInfo?.weight ?? 0,
        },
      }));

      disciplines.push({
        id: discId,
        name: disc.name ?? '',
        startDate: disc.firstClassDate ?? '',
        endDate: disc.lastClassDate ?? '',
        lessons,
      });

      activities.push({
        id: `a_${mi}_${di}`,
        type: EActivityType.Discipline,
        refId: discId,
        moduleId,
        showInDocument: disc.showOnStudentDocument ?? true,
        countsInGrade: disc.isIncludedInGradeCalculation ?? true,
        countsInAttendance: disc.isIncludedInAttendanceCalculation ?? true,
        attendanceByView: true,
        closing: lessons.length === 1 ? 'curso' : 'disciplina',
        moduleWeight: 0,
        defaultGrade: null,
        defaultAttendance: null,
      });
    });
  });

  return {
    id: meta.id,
    name: meta.name,
    course: meta.course ?? '—',
    disciplines,
    modules,
    activities,
  };
}
